import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from './UtilsLayout';

export const SimpleModal = React.memo(
  ({
    isOpen,
    onClose,
    children,
    className,
    bg = '',
    showClose = false,
    draggable = false,
  }) => {
    const modalRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const dragStartRef = useRef({ x: 0, y: 0, startX: 0, startY: 0 });

    // Handle drag start
    const handleMouseDown = (e) => {
      if (!draggable) return;

      setIsDragging(true);
      dragStartRef.current = {
        x: position.x,
        y: position.y,
        startX: e.clientX,
        startY: e.clientY,
      };
    };

    // Handle drag move
    useEffect(() => {
      const handleMouseMove = (e) => {
        if (!isDragging) return;

        const deltaX = e.clientX - dragStartRef.current.startX;
        const deltaY = e.clientY - dragStartRef.current.startY;

        setPosition({
          x: dragStartRef.current.x + deltaX,
          y: dragStartRef.current.y + deltaY,
        });
      };

      const handleMouseUp = () => {
        setIsDragging(false);
      };

      if (isDragging) {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        };
      }
    }, [isDragging]);

    useEffect(() => {
      const handleOutsideClick = (e) => {
        const clickedInsideModal = e.target.closest('.modal-content-wrapper');
        if (!clickedInsideModal) {
          onClose(e);
        }
      };

      if (isOpen) {
        document.body.classList.add('overflow-hidden');
        setTimeout(() => {
          window.addEventListener('mousedown', handleOutsideClick);
        }, 100);

        return () => {
          document.body.classList.remove('overflow-hidden');
          window.removeEventListener('mousedown', handleOutsideClick);
        };
      }
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const modalStyle = draggable
      ? {
          position: 'fixed',
          left: `calc(50% + ${position.x}px)`,
          top: `calc(50% + ${position.y}px)`,
          transform: 'translate(-50%, -50%)',
        }
      : {};

    return createPortal(
      <div
        className={`fixed inset-0 z-[999999] ${draggable ? 'pointer-events-none' : 'bg-black/70 flex items-center justify-center'} ${bg || 'bg-black/60'} px-4`}
      >
        <div
          ref={modalRef}
          onMouseDown={handleMouseDown}
          style={modalStyle}
          className={`${draggable ? 'pointer-events-auto' : 'relative'} modal-content-wrapper z-[9999] text-white p-6 rounded-lg ${draggable ? 'w-[44rem] max-h-[80vh]' : 'w-full max-w-[95vw] max-h-[95vh]'} overflow-auto scrollbar-none ${draggable ? 'cursor-grab active:cursor-grabbing' : ''} ${className}`}
        >
          {onClose && showClose && (
            <CloseIcon
              closeModal={onClose}
              className={'absolute !top-5 bg-transparent'}
            />
          )}
          {children}
        </div>
      </div>,
      document.body
    );
  }
);
